import type { FilterValue } from "@/components/filter-popover";

/**
 * Utility functions for handling FilterValue types consistently across the application.
 *
 * These functions provide a centralized way to extract values from different FilterValue
 * types, handling both legacy string/array formats and new structured filter objects.
 */

// Type guard functions
function hasValueProperty(obj: unknown): obj is { value?: string } {
  return typeof obj === "object" && obj !== null && "value" in obj;
}

function hasValuesProperty(obj: unknown): obj is { values?: string[] } {
  return typeof obj === "object" && obj !== null && "values" in obj;
}

function hasMinMaxProperties(
  obj: unknown,
): obj is { min?: string; max?: string } {
  return (
    typeof obj === "object" && obj !== null && ("min" in obj || "max" in obj)
  );
}

function hasFromToProperties(
  obj: unknown,
): obj is { from?: string; to?: string } {
  return (
    typeof obj === "object" && obj !== null && ("from" in obj || "to" in obj)
  );
}

function hasNullFilterProperty(
  obj: unknown,
): obj is { nullFilter?: "is_null" | "is_not_null" | null } {
  return typeof obj === "object" && obj !== null && "nullFilter" in obj;
}

/**
 * Extracts text content from a FilterValue, handling both string and TextFilter object types
 * @param filterValue The filter value to extract text from
 * @returns The extracted text string, or empty string if no text found
 */
export function extractTextFromFilter(filterValue: FilterValue): string {
  if (typeof filterValue === "string") {
    return filterValue.trim();
  }

  if (hasValueProperty(filterValue)) {
    return (filterValue.value || "").trim();
  }

  return "";
}

/**
 * Extracts array of strings from a FilterValue, handling both array and SelectFilter object types
 * @param filterValue The filter value to extract array from
 * @returns The extracted string array, or empty array if no values found
 */
export function extractArrayFromFilter(filterValue: FilterValue): string[] {
  if (Array.isArray(filterValue)) {
    return filterValue;
  }

  if (hasValuesProperty(filterValue)) {
    return filterValue.values || [];
  }

  return [];
}

/**
 * Extracts number range from a FilterValue, handling NumberFilter object type
 * @param filterValue The filter value to extract number range from
 * @returns Object with min/max values or empty object if no range found
 */
export function extractNumberRangeFromFilter(filterValue: FilterValue): {
  min?: string;
  max?: string;
} {
  if (hasMinMaxProperties(filterValue)) {
    return {
      min: filterValue.min,
      max: filterValue.max,
    };
  }

  return {};
}

/**
 * Extracts date range from a FilterValue, handling DateFilter object type
 * @param filterValue The filter value to extract date range from
 * @returns Object with from/to values or empty object if no range found
 */
export function extractDateRangeFromFilter(filterValue: FilterValue): {
  from?: string;
  to?: string;
} {
  if (hasFromToProperties(filterValue)) {
    return {
      from: filterValue.from,
      to: filterValue.to,
    };
  }

  return {};
}

/**
 * Checks if a FilterValue has a null filter condition (is_null or is_not_null)
 * @param filterValue The filter value to check
 * @returns The null filter condition or null if none set
 */
export function extractNullFilterFromFilter(
  filterValue: FilterValue,
): "is_null" | "is_not_null" | null {
  if (hasNullFilterProperty(filterValue)) {
    return filterValue.nullFilter || null;
  }

  return null;
}

/**
 * Checks if a FilterValue is considered "active" (has meaningful content)
 * @param filterValue The filter value to check
 * @returns True if the filter has active content, false otherwise
 */
export function isFilterValueActive(filterValue: FilterValue): boolean {
  if (!filterValue) return false;

  // Check for null filter conditions
  const nullFilter = extractNullFilterFromFilter(filterValue);
  if (nullFilter === "is_null" || nullFilter === "is_not_null") {
    return true;
  }

  // Check text content
  const textContent = extractTextFromFilter(filterValue);
  if (textContent) return true;

  // Check array content
  const arrayContent = extractArrayFromFilter(filterValue);
  if (arrayContent.length > 0) return true;

  // Check number range
  const numberRange = extractNumberRangeFromFilter(filterValue);
  if (numberRange.min || numberRange.max) return true;

  // Check date range
  const dateRange = extractDateRangeFromFilter(filterValue);
  if (dateRange.from || dateRange.to) return true;

  return false;
}
